/// API Configuration
import CONFIG from './config/config.js';
import DOMPurify from 'dompurify';

// Constants
const DEFAULT_IMAGE = CONFIG.app.images.noImage.fallback;
const NO_IMAGE_PATH = CONFIG.app.images.noImage.path;
const NO_IMAGE_LEGACY_PATH = '/images/icons/no-image2.svg';

function isPlaceholderMenuImageUrl(url) {
    if (!url) return true;
    const u = String(url).toLowerCase();
    return u.includes('/images/icons/no-image') || u.includes('no-image2.svg');
}

/** Facebook hotlinks often fail on mobile; serve via our API instead. */
function isFacebookCdnMenuUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return /^https?:\/\//i.test(url) && /fbcdn\.net|facebook\.com/i.test(url);
}

function menuServeImageUrl(itemId, imageIndex = 0) {
    if (!itemId) return null;
    const baseUrl = CONFIG?.api?.baseUrl || '';
    const idx = imageIndex > 0 ? `?index=${imageIndex}` : '';
    return `${baseUrl}/api/menu/serve-image/${itemId}${idx}`;
}
let hasNoImageSvg = null; // Cache the SVG existence check

// DOM Elements
const menuGrid = document.querySelector('.menu__grid');
const filtersContainer = document.querySelector('.menu__filters');
const searchInput = document.querySelector('.menu__search-input');

// Utility Functions
function sanitizeHTML(str) {
    if (!str) return '';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

function showLoader() {
    menuGrid.innerHTML = `
        <div class="menu__loading">
            <div class="spinner"></div>
            <p>Loading menu items...</p>
        </div>
    `;
}

function showError(message) {
    // WHAT: Sanitize error message before inserting into DOM
    // WHY: Prevent XSS attacks from malicious error messages
    // HOW: Use DOMPurify to sanitize the message
    const sanitizedMessage = DOMPurify.sanitize(message || 'An error occurred');
    menuGrid.innerHTML = `
        <div class="menu__error">
            <p>${sanitizedMessage}</p>
        </div>
    `;
}

/** Normalize menu category for filtering (must match `data-category` on cards). */
function menuCategoryKey(cat) {
    return DOMPurify.sanitize(cat || 'uncategorized');
}

/**
 * Filter full item list by category + search before pagination.
 * Fixes public menu: only the first N items were in the DOM, so other categories looked “broken”.
 */
function filterItemsByCategoryAndSearch(items, category, searchTerm) {
    const searchLower = (searchTerm || '').trim().toLowerCase();
    return items.filter((item) => {
        const key = menuCategoryKey(item.category);
        const matchesCategory = category === 'all' || key === category;
        if (!matchesCategory) return false;
        if (!searchLower) return true;
        const name = (item.name || '').toLowerCase();
        const desc = (item.description || '').toLowerCase();
        return name.includes(searchLower) || desc.includes(searchLower);
    });
}

function getActiveFilterState() {
    const activeBtn = document.querySelector('.menu__filter-btn--active');
    const category = activeBtn?.dataset?.category || 'all';
    const search = searchInput ? searchInput.value.trim() : '';
    return { category, search };
}

function updateLoadMoreButton() {
    const loadMoreButton = document.getElementById('menuLoadMore');
    if (!loadMoreButton) return;
    const { category, search } = getActiveFilterState();
    const filtered = filterItemsByCategoryAndSearch(currentFilteredItems, category, search);
    const hasMore = filtered.length > menuVisibleCount;
    loadMoreButton.style.display = hasMore ? 'inline-flex' : 'none';
}

function renderMenuBatch() {
    const { category, search } = getActiveFilterState();
    const filtered = filterItemsByCategoryAndSearch(currentFilteredItems, category, search);
    const visibleItems = filtered.slice(0, menuVisibleCount);
    renderMenuItems(visibleItems);
    createCategoryFilters(currentFilteredItems, category);
    updateLoadMoreButton();
}

async function checkNoImageExists() {
    if (hasNoImageSvg !== null) return hasNoImageSvg;
    
    try {
        // Try both paths
        const [primaryResponse, legacyResponse] = await Promise.all([
            fetch(NO_IMAGE_PATH, { method: 'HEAD' }).catch(() => ({ ok: false })),
            fetch(NO_IMAGE_LEGACY_PATH, { method: 'HEAD' }).catch(() => ({ ok: false }))
        ]);
        
        hasNoImageSvg = primaryResponse.ok || legacyResponse.ok;
        if (CONFIG.features.debugLogging) {
            console.debug('No-image SVG availability:', {
                primary: primaryResponse.ok,
                legacy: legacyResponse.ok,
                using: hasNoImageSvg ? 'SVG' : 'Base64'
            });
        }
        return hasNoImageSvg;
    } catch (error) {
        console.debug('No-image SVG check failed:', error);
        hasNoImageSvg = false;
        return false;
    }
}

function renderMenuItems(items) {
    if (!items?.length) {
        menuGrid.innerHTML = `
            <div class="menu__no-results">
                <p>No menu items available at the moment.</p>
            </div>
        `;
        return;
    }

    const getImageUrl = (url, imageIndex = 0, itemId = null) => {
        // Make sure CONFIG.api.baseUrl is defined
        const baseUrl = CONFIG?.api?.baseUrl || '';
        
        // If no URL provided, use fallback
        if (!url) {
            return hasNoImageSvg ? NO_IMAGE_PATH : DEFAULT_IMAGE;
        }

        if (url.includes('/api/menu/serve-image/')) {
            return url.startsWith('http') ? url : `${baseUrl}${url}`;
        }

        if (isFacebookCdnMenuUrl(url) && itemId) {
            return menuServeImageUrl(itemId, imageIndex);
        }
        
        // Handle both possible no-image paths
        if (url === NO_IMAGE_PATH || url === NO_IMAGE_LEGACY_PATH) {
            return hasNoImageSvg ? NO_IMAGE_PATH : DEFAULT_IMAGE;
        }
        
        // If it's already a full URL or data URL, use it as is
        if (url.startsWith('http') || url.startsWith('data:')) {
            return url;
        }
        
        // If it's a static image from our site
        if (url.startsWith('/images/')) {
            return url;
        }
        
        // If it's an uploaded image
        if (url.startsWith('/uploads/')) {
            return `${baseUrl}${url}`;
        }
        
        // If it's just a filename, assume it's an upload
        if (!url.startsWith('/')) {
            return `${baseUrl}/uploads/${url}`;
        }
        
        // For any other case, use the URL as is with base
        return `${baseUrl}${url}`;
    };

    // WHAT: Sanitize all user-controlled data before inserting into DOM
    // WHY: Prevent XSS attacks from malicious menu item data
    // HOW: Use DOMPurify to sanitize each field individually (upgraded from basic sanitizeHTML)
    const primaryImageRef = (item) => {
        if (!item?.images?.length) return null;
        const marked = item.images.find(
            (img) => img && typeof img === 'object' && img.isPrimary === true
        );
        const pick = marked || item.images[0];
        if (typeof pick === 'string') return pick;
        return pick?.url || pick?.path || null;
    };

    menuGrid.innerHTML = items.map(item => {
        const sanitizedCategory = menuCategoryKey(item.category);
        const imageUrl = getImageUrl(primaryImageRef(item), 0, item._id);
        const sanitizedImageUrl = DOMPurify.sanitize(imageUrl);
        const primaryRef = item.images?.find((img) => img && typeof img === 'object' && img.isPrimary) || item.images?.[0];
        const sanitizedImageAlt = DOMPurify.sanitize(
            (typeof primaryRef === 'object' && primaryRef?.altText) || `${item.name} image`
        );
        const sanitizedName = DOMPurify.sanitize(item.name || 'Unnamed Item');
        const sanitizedDescription = DOMPurify.sanitize(item.description || '');
        const priceDisplay = item.formattedPrice || `$${Number(item.price).toFixed(2)}`;
        const sanitizedPrice = DOMPurify.sanitize(priceDisplay);
        const sanitizedAllergens = item.allergens && item.allergens.length > 0 ? 
            item.allergens.map(a => DOMPurify.sanitize(a.charAt(0).toUpperCase() + a.slice(1))).join(', ') 
            : '';
        
        // Real photos only — skip placeholder SVG left from create-before-Facebook-attach
        const allImages = item.images && Array.isArray(item.images) && item.images.length > 0
            ? item.images
                  .map((img, imageIndex) => {
                      const imgUrl = typeof img === 'string' ? img : img.url || img.path || null;
                      return imgUrl ? getImageUrl(imgUrl, imageIndex, item._id) : null;
                  })
                  .filter((url) => url && !isPlaceholderMenuImageUrl(url))
            : [];

        const primaryResolved =
            sanitizedImageUrl && !isPlaceholderMenuImageUrl(sanitizedImageUrl)
                ? sanitizedImageUrl
                : null;
        const orderedImages =
            primaryResolved && allImages.includes(primaryResolved)
                ? [primaryResolved, ...allImages.filter((u) => u !== primaryResolved)]
                : primaryResolved
                  ? [primaryResolved, ...allImages]
                  : allImages;

        const primaryImageUrl = orderedImages[0] || NO_IMAGE_PATH;
        const hasMultipleImages = orderedImages.length > 1;
        
        // Create dots for multiple images
        const imageDots = hasMultipleImages ? `
            <div class="menu__item-image-dots">
                ${allImages.map((_, index) => `
                    <button 
                        class="menu__item-image-dot ${index === 0 ? 'active' : ''}" 
                        data-image-index="${index}"
                        aria-label="View image ${index + 1} of ${allImages.length}"
                        type="button"
                    ></button>
                `).join('')}
            </div>
        ` : '';
        
        return `
            <article class="menu__item" data-category="${sanitizedCategory}" data-item-id="${item._id || ''}">
                <div class="menu__item-image-container">
                    <div class="menu__item-image">
                        <img 
                            src="${primaryImageUrl}" 
                            alt="${sanitizedImageAlt}"
                            loading="lazy"
                            ${primaryImageUrl.startsWith('http') ? 'referrerpolicy="no-referrer"' : ''}
                            data-all-images='${JSON.stringify(orderedImages)}'
                            onerror="this.onerror=null; this.src='${NO_IMAGE_PATH}';"
                        >
                    </div>
                    ${imageDots}
                </div>
                <div class="menu__item-content">
                    <h2 class="menu__item-title">${sanitizedName}</h2>
                    <p class="menu__item-description">${sanitizedDescription}</p>
                    <p class="menu__item-price">${sanitizedPrice}</p>
                    ${sanitizedAllergens ? 
                        `<p class="menu__item-allergens">Contains: ${sanitizedAllergens}</p>` 
                        : ''
                    }
                </div>
            </article>
        `;
    }).join('');
    
    // Add event listeners for image dots (carousel) after HTML is inserted
    setTimeout(() => {
        const imageDots = menuGrid.querySelectorAll('.menu__item-image-dot');
        imageDots.forEach((dot) => {
            dot.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                const menuItem = dot.closest('.menu__item');
                const img = menuItem.querySelector('.menu__item-image img');
                const allImages = JSON.parse(img.getAttribute('data-all-images') || '[]');
                const targetIndex = parseInt(dot.getAttribute('data-image-index') || '0');
                
                if (allImages[targetIndex]) {
                    // Update image source with fade effect
                    img.style.opacity = '0';
                    setTimeout(() => {
                        img.src = allImages[targetIndex];
                        img.style.opacity = '1';
                    }, 150);
                    
                    // Update active dot
                    menuItem.querySelectorAll('.menu__item-image-dot').forEach((d, i) => {
                        d.classList.toggle('active', i === targetIndex);
                    });
                }
            });
        });
    }, 0);
}

function createCategoryFilters(items, activeCategory = 'all') {
    const categories = [
        ...new Set(
            items.map((item) => menuCategoryKey(item.category))
        )
    ].filter(Boolean);

    const allActive = activeCategory === 'all' ? 'menu__filter-btn--active' : '';

    filtersContainer.innerHTML = `
        <button 
            type="button"
            class="menu__filter-btn ${allActive}" 
            data-category="all"
        >
            All Items
        </button>
        ${categories.map((category) => {
            const sanitizedCategory = menuCategoryKey(category);
            const isActive =
                activeCategory === sanitizedCategory
                    ? 'menu__filter-btn--active'
                    : '';
            return `
                <button 
                    type="button"
                    class="menu__filter-btn ${isActive}" 
                    data-category="${sanitizedCategory}"
                >
                    ${sanitizedCategory}
                </button>
            `;
        }).join('')}
    `;
}

function bindMenuFilterClicksOnce() {
    if (!filtersContainer || filtersContainer.dataset.filterClickBound === '1') return;
    filtersContainer.dataset.filterClickBound = '1';
    filtersContainer.addEventListener('click', (e) => {
        const btn = e.target.closest('.menu__filter-btn');
        if (!btn) return;
        document.querySelectorAll('.menu__filter-btn').forEach((b) => {
            b.classList.remove('menu__filter-btn--active');
        });
        btn.classList.add('menu__filter-btn--active');
        menuVisibleCount = menuBatchSize;
        renderMenuBatch();
    });
}

/** Search box: reset pagination and re-render from full filtered list. */
function onMenuSearchInput() {
    menuVisibleCount = menuBatchSize;
    renderMenuBatch();
}

// State
let allMenuItems = []; // Store all items (site + Square)
/** While open — which sources to show (subset of site/square). Loaded from menuSourcesOpenHours. */
let currentOpenHoursMenuSources = ['site', 'square'];
let currentFilteredItems = [];
let menuBatchSize = 12;
let menuVisibleCount = 12;
/** Interval id when closed + curated + rotation cycles item order on the public menu */
let closedMenuPoolRotationTimer = null;

function clearClosedMenuPoolRotation() {
    if (closedMenuPoolRotationTimer) {
        clearInterval(closedMenuPoolRotationTimer);
        closedMenuPoolRotationTimer = null;
    }
}

/**
 * Rotate in-memory order of closed-hours curated items so a different slice leads filters / load-more.
 */
function maybeStartClosedMenuRotation(menuMode, closedMenuSource, rotationEnabled) {
    clearClosedMenuPoolRotation();
    if (menuMode !== 'fallback' || closedMenuSource !== 'curated' || !rotationEnabled) return;
    if (!Array.isArray(allMenuItems) || allMenuItems.length < 2) return;
    closedMenuPoolRotationTimer = setInterval(() => {
        const first = allMenuItems.shift();
        if (first) allMenuItems.push(first);
        updateMenuDisplay();
    }, 45000);
}

/**
 * Public menu mode from server (manual business hours): live = Square+site mix; fallback = closed-hours list
 */
async function fetchPublicMenuMode() {
    try {
        const response = await fetch(`${CONFIG.api.baseUrl}/api/menu/public-mode`, {
            credentials: 'include',
            headers: { Accept: 'application/json' }
        });
        if (!response.ok) {
            return { menuMode: 'fallback' };
        }
        const result = await response.json();
        return result.data || { menuMode: 'fallback' };
    } catch (error) {
        if (CONFIG.features.debugLogging) {
            console.debug('Public menu mode unavailable, using fallback:', error);
        }
        return { menuMode: 'fallback' };
    }
}

async function fetchClosedFallbackItems() {
    try {
        const response = await fetch(`${CONFIG.api.baseUrl}/api/menu/closed-fallback-items`, {
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Cache-Control': 'no-cache'
            }
        });
        if (!response.ok) {
            return [];
        }
        const result = await response.json();
        return result.success && Array.isArray(result.data) ? result.data : [];
    } catch (error) {
        if (CONFIG.features.debugLogging) {
            console.debug('Closed fallback menu fetch failed:', error);
        }
        return [];
    }
}

/** Closed/live menu hints are admin-only; keep the public menu header clean. */
function setMenuStatusMessage(_message) {
    const el = document.getElementById('menuStatusMessage');
    if (el) {
        el.remove();
    }
}

// Event Handlers
async function fetchSquareItems() {
    try {
        const response = await fetch(`${CONFIG.api.baseUrl}/api/square/items`, {
            headers: {
                'Accept': 'application/json',
                'Cache-Control': 'no-cache'
            },
            credentials: 'include'
        });

        if (!response.ok) {
            // Square API might not be configured - that's okay
            if (CONFIG.features.debugLogging) {
                console.debug('Square API not available or not configured');
            }
            return [];
        }

        const result = await response.json();
        if (result.success && result.data) {
            // Mark items as from Square
            return result.data.map(item => ({
                ...item,
                syncSource: 'square',
                source: 'square'
            }));
        }
        return [];
    } catch (error) {
        if (CONFIG.features.debugLogging) {
            console.debug('Error fetching Square items:', error);
        }
        return [];
    }
}

async function fetchSiteItems() {
    try {
        const response = await fetch(`${CONFIG.api.baseUrl}/api/menu`, {
            headers: {
                'Accept': 'application/json',
                'Cache-Control': 'no-cache'
            },
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to load menu (Status: ${response.status})`);
        }

        let result;
        try {
            const text = await response.text();
            if (CONFIG.features.debugLogging) {
                console.debug('Raw menu response:', text);
            }
            result = JSON.parse(text);
        } catch (error) {
            console.error('Failed to parse menu response:', error);
            throw new Error('Invalid menu data received from server');
        }

        if (!result?.success) {
            throw new Error(result?.message || 'Failed to load menu items');
        }

        // Mark items as from site - filter out Square items (they come from fetchSquareItems)
        return (result.data || [])
            .filter(item => {
                // Only include items that are NOT from Square
                // Items with syncSource 'square' should come from fetchSquareItems, not here
                return item.syncSource !== 'square';
            })
            .map(item => ({
                ...item,
                syncSource: item.syncSource || 'site',
                source: 'site'
            }));
    } catch (error) {
        console.error('Error fetching site menu items:', error);
        return [];
    }
}

/** @param {{ menuSourcesOpenHours?: string[], displaySource?: string }} data */
function openHoursMenuSourcesFromConfigData(data) {
    if (Array.isArray(data?.menuSourcesOpenHours)) {
        const picked = data.menuSourcesOpenHours.filter((x) => x === 'site' || x === 'square');
        if (picked.length > 0) return picked;
        // Empty array in DB = use legacy displaySource (avoid hiding entire menu by mistake)
    }
    const ds = data?.displaySource || 'all';
    if (ds === 'all') return ['site', 'square'];
    if (ds === 'site') return ['site'];
    if (ds === 'square') return ['square'];
    return ['site', 'square'];
}

function filterItemsByOpenHourSources(items, sources) {
    if (!sources || sources.length === 0) return [];
    const allowed = new Set(sources);
    return items.filter((item) => {
        const itemSource = item.source || (item.syncSource === 'square' ? 'square' : 'site');
        return allowed.has(itemSource);
    });
}

function updateMenuDisplay() {
    const filteredItems = filterItemsByOpenHourSources(allMenuItems, currentOpenHoursMenuSources);
    currentFilteredItems = filteredItems;
    menuVisibleCount = menuBatchSize;
    renderMenuBatch();
}

async function initializeMenu() {
    try {
        if (CONFIG.features.debugLogging) {
            console.debug('Initializing menu with configuration:', {
                apiBase: CONFIG.api.baseUrl,
                endpoints: CONFIG.endpoints,
                features: CONFIG.features
            });
        }

        showLoader();
        
        const [modePayload, configResponse] = await Promise.all([
            fetchPublicMenuMode(),
            fetch(`${CONFIG.api.baseUrl}/api/menu/display-config`, { credentials: 'include' })
        ]);
        
        const menuMode = modePayload?.menuMode === 'live' ? 'live' : 'fallback';
        
        /** @type {'curated'|'site_all'|'site_sample'} */
        let closedMenuSource = 'curated';
        let closedMenuRotationEnabled = false;
        try {
            if (configResponse.ok) {
                const config = await configResponse.json();
                currentOpenHoursMenuSources = openHoursMenuSourcesFromConfigData(config.data || {});
                const c = config.data?.closedMenuSource;
                if (c === 'site_all' || c === 'site_sample') {
                    closedMenuSource = c;
                } else {
                    closedMenuSource = 'curated';
                }
                closedMenuRotationEnabled = Boolean(config.data?.closedMenuRotationEnabled);
            } else {
                currentOpenHoursMenuSources = ['site', 'square'];
            }
        } catch (error) {
            currentOpenHoursMenuSources = ['site', 'square'];
        }

        clearClosedMenuPoolRotation();
        
        await checkNoImageExists();
        
        if (menuMode === 'live') {
            const [siteItems, squareItems] = await Promise.all([
                fetchSiteItems(),
                fetchSquareItems()
            ]);
            allMenuItems = [...siteItems, ...squareItems];
            setMenuStatusMessage('');
        } else {
            if (closedMenuSource === 'site_all') {
                const siteOnly = await fetchSiteItems();
                allMenuItems = siteOnly.map(item => ({
                    ...item,
                    syncSource: item.syncSource || 'site',
                    source: 'site'
                }));
                setMenuStatusMessage('We\'re closed right now — showing our site menu. Visit during open hours for the live Square menu.');
                currentOpenHoursMenuSources = ['site', 'square'];
            } else if (closedMenuSource === 'site_sample') {
                const siteOnly = await fetchSiteItems();
                allMenuItems = siteOnly.slice(0, 25).map(item => ({
                    ...item,
                    syncSource: item.syncSource || 'site',
                    source: 'site'
                }));
                setMenuStatusMessage('We\'re closed right now — sample of our site menu. Open hours show the full live menu.');
                currentOpenHoursMenuSources = ['site', 'square'];
            } else {
                let closedItems = await fetchClosedFallbackItems();
                closedItems = closedItems.map(item => ({
                    ...item,
                    syncSource: item.syncSource || 'site',
                    source: 'site'
                }));
                if (closedItems.length === 0) {
                    const siteOnly = await fetchSiteItems();
                    closedItems = siteOnly.slice(0, 25);
                    setMenuStatusMessage('We\'re closed right now — showing a sample of our menu. Pick closed-menu favorites in admin or choose another closed option.');
                } else {
                    setMenuStatusMessage('We\'re closed right now — here are your chosen favorites. Open hours show the live menu.');
                }
                allMenuItems = closedItems;
                currentOpenHoursMenuSources = ['site', 'square'];
            }
        }
        
        // Defensive filter: only show active items
        allMenuItems = allMenuItems.filter(item => item.isActive !== false);
        
        if (menuMode === 'live' && allMenuItems.length === 0) {
            const fallbackItems = await fetchClosedFallbackItems();
            if (fallbackItems.length > 0) {
                allMenuItems = fallbackItems
                    .map(item => ({
                        ...item,
                        syncSource: item.syncSource || 'site',
                        source: 'site'
                    }))
                    .filter(item => item.isActive !== false);
                setMenuStatusMessage('Menu is updating — showing favorites in the meantime.');
            } else {
                const siteOnly = await fetchSiteItems();
                allMenuItems = siteOnly.slice(0, 25).filter(item => item.isActive !== false);
                if (allMenuItems.length > 0) {
                    setMenuStatusMessage('Menu is updating — showing a sample of our items.');
                }
            }
        }
        
        // Display based on admin setting (no toggle on public page)
        updateMenuDisplay();

        maybeStartClosedMenuRotation(menuMode, closedMenuSource, closedMenuRotationEnabled);

        bindMenuFilterClicksOnce();

        const loadMoreButton = document.getElementById('menuLoadMore');
        if (loadMoreButton) {
            loadMoreButton.addEventListener('click', () => {
                menuVisibleCount += menuBatchSize;
                renderMenuBatch();
            });
        }
        
    } catch (error) {
        console.error('Error loading menu:', error);
        if (CONFIG.features.debugLogging) {
            console.debug('Menu loading error details:', error);
        }
        showError('Sorry, we couldn\'t load the menu. Please try again later.');
    }
}

// Initializes
// Note: This file is now imported through menu-init.js, so we don't need the DOMContentLoaded listener here
// The initialization will be handled by the importing module

// Export the initialization function for use by other modules
export { initializeMenu, checkNoImageExists, onMenuSearchInput };